import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";
import { useBranding } from "../../../contexts/BrandingContext";
import { getBaseURL } from "../../../services/api";

/**
 * SEO component managing meta tags, Open Graph, Twitter Cards, and structured data
 * Combines page-specific SEO data with global SEO settings and branding context
 *
 * @component
 * @param {Object} props
 * @param {Object} [props.seoData] - Page-specific SEO data
 * @param {Object} [props.globalSeo] - Global SEO settings
 */
const SEO = ({ seoData, globalSeo }) => {
  const { siteName } = useBranding();

  // Get SEO data from page or global settings
  const title = seoData?.metaTitle || globalSeo?.metaTitle;
  const description = seoData?.metaDescription || globalSeo?.metaDescription;
  const keywords = seoData?.keywords || globalSeo?.keywords;
  const canonicalURL = seoData?.canonicalURL || globalSeo?.canonicalURL;
  const metaRobots = seoData?.metaRobots || globalSeo?.metaRobots;
  const metaViewport = seoData?.metaViewport || globalSeo?.metaViewport;

  // Social media tags
  const metaImage = seoData?.metaImage?.url || globalSeo?.metaImage?.url;
  const openGraphData = seoData?.openGraph?.[0] || globalSeo?.openGraph?.[0];

  const resolveAssetUrl = (url) => {
    if (!url) {
      return null;
    }
    return url.startsWith("/") ? `${getBaseURL()}${url}` : url;
  };

  const openGraphImage = openGraphData?.image?.url || openGraphData?.ogImage?.url || null;
  const resolvedMetaImage = resolveAssetUrl(metaImage);
  const resolvedOpenGraphImage = resolveAssetUrl(openGraphImage);

  // Structured data
  const customStructuredData = seoData?.structuredData || globalSeo?.structuredData;

  // Require title to render
  if (!title) {
    return null;
  }

  return (
    <Helmet>
      <meta charSet="utf-8" />

      {/* Meta tags */}
      <title>EyeOnU</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {metaRobots && <meta name="robots" content={metaRobots} />}
      {metaViewport && <meta name="viewport" content={metaViewport} />}
      {canonicalURL && <link rel="canonical" href={canonicalURL} />}

      {/* Open Graph */}
      {openGraphData && (
        <>
          <meta property="og:title" content={openGraphData.ogTitle || openGraphData.title || title} />
          <meta
            property="og:description"
            content={openGraphData.ogDescription || openGraphData.description || description}
          />
          <meta property="og:type" content={openGraphData.ogType || openGraphData.type || "website"} />
          {(openGraphData.ogUrl || openGraphData.url) && (
            <meta property="og:url" content={openGraphData.ogUrl || openGraphData.url} />
          )}
          {(resolvedOpenGraphImage || resolvedMetaImage) && (
            <meta property="og:image" content={resolvedOpenGraphImage || resolvedMetaImage} />
          )}
          <meta property="og:site_name" content={siteName} />
        </>
      )}

      {/* Twitter Cards */}
      {resolvedMetaImage && (
        <>
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={title} />
          {description && <meta name="twitter:description" content={description} />}
          <meta name="twitter:image" content={resolvedMetaImage} />
        </>
      )}

      {/* JSON-LD */}
      {customStructuredData && (
        <script type="application/ld+json">
          {typeof customStructuredData === "string" ? customStructuredData : JSON.stringify(customStructuredData)}
        </script>
      )}
    </Helmet>
  );
};

SEO.propTypes = {
  seoData: PropTypes.object,
  globalSeo: PropTypes.object,
};

SEO.defaultProps = {
  seoData: null,
  globalSeo: null,
};

export default SEO;
